import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { Heart, MessageCircle, Repeat2, Send } from "lucide-react";
import { getBackgroundStyle, getSelectedPreset } from "@/lib/previewStyles";
import type { BeautifierSettings, PostContent } from "@/lib/types";

type PreviewCanvasProps = {
  post: PostContent;
  settings: BeautifierSettings;
};

export const PreviewCanvas = forwardRef<HTMLDivElement, PreviewCanvasProps>(
  function PreviewCanvas({ post, settings }, ref) {
    const shellRef = useRef<HTMLDivElement>(null);
    const preset = getSelectedPreset(settings);
    const [scale, setScale] = useState(0.58);
    const [postImageRatio, setPostImageRatio] = useState(1.85);

    useEffect(() => {
      if (!shellRef.current) {
        return;
      }

      const observer = new ResizeObserver(([entry]) => {
        const availableWidth = Math.max(280, entry.contentRect.width - 32);
        const nextScale = Math.min(1, Math.max(0.2, availableWidth / preset.width));
        setScale(nextScale);
      });

      observer.observe(shellRef.current);
      return () => observer.disconnect();
    }, [preset.width]);

    useEffect(() => {
      if (!post.postImage) {
        setPostImageRatio(1.85);
        return;
      }

      const image = new Image();
      image.crossOrigin = "anonymous";
      image.onload = () => {
        if (image.naturalWidth > 0 && image.naturalHeight > 0) {
          setPostImageRatio(image.naturalWidth / image.naturalHeight);
        }
      };
      image.src = post.postImage;
    }, [post.postImage]);

    const backgroundStyle = getBackgroundStyle(settings);
    const cardBackground = hexToRgba(
      settings.cardBackgroundColor,
      settings.cardOpacity / 100,
    );
    const cardWidth = Math.max(300, preset.width - settings.padding * 2);
    const cardMaxHeight = Math.max(320, preset.height - settings.padding * 2);
    const isCompact = preset.height <= 720;
    const hasPostImage = Boolean(post.postImage);
    const { body, hashtags } = splitPostText(post.text);
    const bodyFontSize = getBodyFontSize(post.text, preset.height, hasPostImage);
    const headlineFontSize = isCompact ? 16 : 19;
    const avatarSize = isCompact ? 58 : 72;
    const headerPadding = isCompact ? 22 : 28;
    const postImageMaxHeight = getPostImageMaxHeight(
      cardMaxHeight,
      settings.postImageHeight,
      preset.width / preset.height,
      isCompact,
    );
    const postImageFrame = getPostImageFrame({
      fit: settings.postImageFit,
      maxHeight: postImageMaxHeight,
      ratio: postImageRatio,
      width: cardWidth - 64,
    });
    const textLineClamp = getLineClamp(
      post.text,
      preset.height,
      hasPostImage,
      Boolean(hashtags),
    );
    const textBoxWidth = cardWidth - 64;
    const bodyPreview = truncateTextForBox({
      fontSize: bodyFontSize,
      lineClamp: textLineClamp,
      text:
        body || "Paste post text to create a polished image preview.",
      width: textBoxWidth,
    });
    const hashtagsPreview = hashtags
      ? truncateTextForBox({
          fontSize: bodyFontSize,
          lineClamp: 2,
          text: hashtags,
          width: textBoxWidth,
        })
      : "";
    const profileHeaderHeight = post.profileHeaderImage
      ? Math.max(isCompact ? 60 : 84, Math.round(cardMaxHeight * 0.13))
      : 0;

    const canvasStyle: CSSProperties = {
      width: preset.width,
      height: preset.height,
      padding: settings.padding,
      ...backgroundStyle,
    };

    const cardStyle: CSSProperties = {
      width: cardWidth,
      height: cardMaxHeight,
      borderRadius: settings.borderRadius,
      backgroundColor: cardBackground,
      color: settings.textColor,
      boxShadow:
        settings.shadowIntensity === 0
          ? "none"
          : `0 ${Math.max(10, settings.shadowIntensity)}px ${Math.max(
              24,
              settings.shadowIntensity * 2,
            )}px rgba(15, 23, 42, ${Math.min(
              0.34,
              settings.shadowIntensity / 180,
            )})`,
    };

    return (
      <div
        ref={shellRef}
        className="checkerboard overflow-hidden rounded-lg border border-slate-200 bg-slate-100 p-3"
      >
        <div
          className="mx-auto"
          style={{
            width: preset.width * scale,
            height: preset.height * scale,
          }}
        >
          <div
            style={{
              width: preset.width,
              height: preset.height,
              transform: `scale(${scale})`,
              transformOrigin: "top left",
            }}
          >
            <div
              ref={ref}
              className="flex items-center justify-center overflow-hidden"
              style={canvasStyle}
            >
              <article
                className="flex flex-col overflow-hidden border border-white/65 backdrop-blur-sm"
                style={cardStyle}
              >
                {post.profileHeaderImage ? (
                  <div
                    className="shrink-0 overflow-hidden border-b border-current/10 bg-slate-100"
                    style={{ height: profileHeaderHeight }}
                  >
                    <img
                      src={post.profileHeaderImage}
                      alt=""
                      className="h-full w-full object-cover"
                      crossOrigin="anonymous"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                ) : null}

                <header
                  className="flex shrink-0 items-start gap-5"
                  style={{
                    padding: headerPadding,
                    paddingBottom: isCompact ? 14 : 18,
                  }}
                >
                  <Avatar
                    src={post.profileImage}
                    name={post.authorName}
                    size={avatarSize}
                  />
                  <div className="min-w-0 flex-1">
                    <div
                      className="truncate font-bold leading-tight"
                      style={{ fontSize: isCompact ? 24 : 30 }}
                    >
                      {post.authorName || "Author name"}
                    </div>
                    <div
                      className="mt-1 line-clamp-2 leading-snug opacity-[0.78]"
                      style={{ fontSize: headlineFontSize }}
                    >
                      {post.authorHeadline || "Author headline"}
                    </div>
                    <div
                      className="mt-2 text-current opacity-60"
                      style={{ fontSize: isCompact ? 14 : 16 }}
                    >
                      {post.timestamp || "Now"} - Public
                    </div>
                  </div>
                </header>

                <div className="flex min-h-0 flex-1 flex-col">
                  <div
                    className="min-h-0 px-7 pb-5 leading-relaxed sm:px-8"
                    style={{ fontSize: bodyFontSize }}
                  >
                    <div
                      className="whitespace-pre-wrap"
                      style={{
                        overflow: "hidden",
                      }}
                    >
                      {bodyPreview}
                    </div>
                    {hashtags ? (
                      <div
                        className="mt-3 font-semibold text-[#0A66C2]"
                        style={{
                          overflow: "hidden",
                        }}
                      >
                        {hashtagsPreview}
                      </div>
                    ) : null}
                  </div>

                  {post.postImage ? (
                    <div
                      className="mx-7 mb-6 shrink-0 overflow-hidden rounded-xl border border-current/10 bg-white sm:mx-8"
                      style={{ height: postImageFrame.height }}
                    >
                      <img
                        src={post.postImage}
                        alt=""
                        className="h-full w-full"
                        style={{
                          objectFit: postImageFrame.objectFit,
                          objectPosition: "center",
                        }}
                        crossOrigin="anonymous"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  ) : null}
                </div>

                <footer
                  className="mt-auto shrink-0 border-t border-current/10 px-8 py-5"
                  style={{ fontSize: isCompact ? 14 : 16 }}
                >
                  <div className="flex items-center justify-between gap-4 text-current opacity-[0.68]">
                    <EngagementItem icon={Heart} label="Inspired" />
                    <EngagementItem icon={MessageCircle} label="Comment" />
                    <EngagementItem icon={Repeat2} label="Repost" />
                    <EngagementItem icon={Send} label="Send" />
                  </div>
                </footer>
              </article>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

function Avatar({
  src,
  name,
  size,
}: {
  src: string;
  name: string;
  size: number;
}) {
  const initials = getInitials(name);
  const style: CSSProperties = {
    width: size,
    height: size,
    fontSize: Math.max(18, Math.round(size * 0.34)),
  };

  if (src) {
    return (
      <img
        src={src}
        alt=""
        className="shrink-0 rounded-full object-cover ring-4 ring-white/75"
        style={style}
        crossOrigin="anonymous"
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <div
      className="grid shrink-0 place-items-center rounded-full bg-gradient-to-br from-ocean via-mint to-amber font-bold text-white ring-4 ring-white/75"
      style={style}
    >
      {initials}
    </div>
  );
}

function EngagementItem({
  icon: Icon,
  label,
}: {
  icon: typeof Heart;
  label: string;
}) {
  return (
    <span className="inline-flex min-w-0 items-center gap-2 font-semibold">
      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </span>
  );
}

function getInitials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return "PS";
  }

  return parts
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

function hexToRgba(hex: string, alpha: number) {
  const normalized = hex.replace("#", "");

  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return `rgba(255,255,255,${alpha})`;
  }

  const red = parseInt(normalized.slice(0, 2), 16);
  const green = parseInt(normalized.slice(2, 4), 16);
  const blue = parseInt(normalized.slice(4, 6), 16);

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function getBodyFontSize(text: string, height: number, hasPostImage: boolean) {
  const length = text.trim().length;

  if (height <= 720) {
    if (hasPostImage) {
      return length > 600 ? 17 : 19;
    }

    if (length > 700) {
      return 21;
    }

    if (length > 430) {
      return 24;
    }

    return 30;
  }

  if (hasPostImage) {
    if (length > 900) {
      return 20;
    }

    if (length > 560) {
      return 22;
    }

    return 27;
  }

  if (length > 900) {
    return 23;
  }

  if (length > 560) {
    return 27;
  }

  return 34;
}

function getLineClamp(
  text: string,
  height: number,
  hasPostImage: boolean,
  hasHashtags: boolean,
) {
  if (!hasPostImage) {
    return height <= 720 ? 9 : height >= 1350 ? 24 : 16;
  }

  const length = text.trim().length;
  const hashtagReserve = hasHashtags ? 2 : 0;

  if (height <= 720) {
    return Math.max(3, (length > 700 ? 5 : 6) - hashtagReserve);
  }

  if (height >= 1600) {
    return 14 - hashtagReserve;
  }

  if (height >= 1200) {
    return 9 - hashtagReserve;
  }

  return Math.max(4, 6 - hashtagReserve);
}

function getPostImageMaxHeight(
  cardMaxHeight: number,
  imageArea: number,
  canvasRatio: number,
  isCompact: boolean,
) {
  const requestedHeight = Math.round(cardMaxHeight * (imageArea / 100));

  if (isCompact) {
    return Math.max(120, Math.min(requestedHeight, Math.round(cardMaxHeight * 0.5)));
  }

  if (canvasRatio < 0.7) {
    return Math.max(280, Math.min(requestedHeight, Math.round(cardMaxHeight * 0.52)));
  }

  return Math.max(180, Math.min(requestedHeight, Math.round(cardMaxHeight * 0.48)));
}

function getPostImageFrame({
  fit,
  maxHeight,
  ratio,
  width,
}: {
  fit: BeautifierSettings["postImageFit"];
  maxHeight: number;
  ratio: number;
  width: number;
}) {
  if (fit === "fill") {
    return {
      height: maxHeight,
      objectFit: "cover" as const,
    };
  }

  if (fit === "stretch") {
    return {
      height: maxHeight,
      objectFit: "fill" as const,
    };
  }

  const naturalHeight = Math.round(width / Math.max(0.2, ratio));

  return {
    height: Math.min(maxHeight, Math.max(80, naturalHeight)),
    objectFit: "contain" as const,
  };
}

function truncateTextForBox({
  fontSize,
  lineClamp,
  text,
  width,
}: {
  fontSize: number;
  lineClamp: number;
  text: string;
  width: number;
}) {
  const normalized = text.trim();

  if (!normalized) {
    return "";
  }

  const charsPerLine = Math.max(16, Math.floor(width / (fontSize * 0.53)));
  const newlinePenalty = Math.max(8, Math.floor(charsPerLine * 0.72));
  const budget = Math.max(
    24,
    Math.floor(charsPerLine * lineClamp * 0.88),
  );
  let weightedLength = 0;

  for (let index = 0; index < normalized.length; index += 1) {
    weightedLength += normalized[index] === "\n" ? newlinePenalty : 1;

    if (weightedLength > budget) {
      return `${trimAtWord(normalized.slice(0, index))}...`;
    }
  }

  return normalized;
}

function trimAtWord(value: string) {
  const trimmed = value.trimEnd();
  const lastSpace = Math.max(
    trimmed.lastIndexOf(" "),
    trimmed.lastIndexOf("\n"),
    trimmed.lastIndexOf("\t"),
  );

  if (lastSpace > Math.floor(trimmed.length * 0.72)) {
    return trimmed.slice(0, lastSpace).trimEnd();
  }

  return trimmed.replace(/[.,;:!?-]+$/g, "").trimEnd();
}

function splitPostText(text: string) {
  const lines = text.trim().split("\n");
  const hashtagLines: string[] = [];

  while (lines.length > 0 && isHashtagLine(lines[lines.length - 1])) {
    const line = lines.pop();

    if (line) {
      hashtagLines.unshift(line.trim());
    }
  }

  return {
    body: lines.join("\n").trim(),
    hashtags: hashtagLines.join(" ").trim(),
  };
}

function isHashtagLine(line: string) {
  const trimmed = line.trim();

  if (!trimmed.startsWith("#")) {
    return false;
  }

  return trimmed
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => word.startsWith("#"));
}
